import CelebrationIcon from '@mui/icons-material/Celebration';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import ChurchIcon from '@mui/icons-material/Church';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import LyricsIcon from '@mui/icons-material/Lyrics';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import TagIcon from '@mui/icons-material/Tag';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ShortcutHint } from './ShortcutHint';
import * as styles from './SimilarTracksModal.styles';
import type { NotSoundReason } from './types';

interface NotSoundReasonDialogProps {
  open: boolean;
  trackTitle: string;
  otherText: string;
  showOtherInput: boolean;
  onClose: () => void;
  onSelectReason: (reason: NotSoundReason) => void;
  onOtherTextChange: (text: string) => void;
  onOtherSubmit: () => void;
}

export const NotSoundReasonDialog = ({
  open,
  trackTitle,
  otherText,
  showOtherInput,
  onClose,
  onSelectReason,
  onOtherTextChange,
  onOtherSubmit,
}: NotSoundReasonDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>What's the reason this track doesn't fit?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={styles.dialogContentText}>
          Please select the specific reason why "{trackTitle}" isn't a good
          match:
        </DialogContentText>
        <Box sx={styles.feedbackButtonsContainer}>
          <Box sx={styles.notSoundGrid}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => onSelectReason('holiday')}
              startIcon={<CelebrationIcon />}
              sx={styles.feedbackButton}
            >
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Holiday
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The track is holiday-themed
                </Typography>
                <ShortcutHint label="h" />
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => onSelectReason('childrens')}
              startIcon={<ChildCareIcon />}
              sx={styles.feedbackButton}
            >
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Children's
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The track is children's music
                </Typography>
                <ShortcutHint label="c" />
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => onSelectReason('devotional')}
              startIcon={<ChurchIcon />}
              sx={styles.feedbackButton}
            >
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Devotional
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The track is religious music
                </Typography>
                <ShortcutHint label="v" />
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => onSelectReason('explicit')}
              startIcon={<TagIcon />}
              sx={styles.feedbackButton}
            >
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Explicit
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  One is explicit
                </Typography>
                <ShortcutHint label="x" />
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => onSelectReason('wrong_language')}
              startIcon={<GTranslateIcon />}
              sx={styles.feedbackButton}
            >
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Wrong language
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Language mismatch
                </Typography>
                <ShortcutHint label="w" />
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => onSelectReason('lyrics')}
              startIcon={<LyricsIcon />}
              sx={styles.feedbackButton}
            >
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Lyrics
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Lyrical themes don't match
                </Typography>
                <ShortcutHint label="l" />
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => onSelectReason('not_popular')}
              startIcon={<TagIcon />}
              sx={styles.feedbackButton}
            >
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Not popular
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Track is not well-known
                </Typography>
                <ShortcutHint label="p" />
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => onSelectReason('bad_quality')}
              startIcon={<TagIcon />}
              sx={styles.feedbackButton}
            >
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Bad quality
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Poor audio quality
                </Typography>
                <ShortcutHint label="q" />
              </Box>
            </Button>
          </Box>
          {/* Other option at the bottom, full width */}
          <Button
            variant="outlined"
            fullWidth
            onClick={() => onSelectReason('other')}
            startIcon={<MoreHorizIcon />}
            sx={styles.feedbackButton}
          >
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Other
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Another reason (please specify)
              </Typography>
              <ShortcutHint label="o" />
            </Box>
          </Button>
          {showOtherInput && (
            <Box sx={styles.otherInputContainer}>
              <TextField
                fullWidth
                label="Please specify the reason"
                placeholder="e.g., Genre mismatch, Cultural context, etc."
                value={otherText}
                onChange={(e) => onOtherTextChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (otherText.trim()) {
                      onOtherSubmit();
                    }
                  }
                }}
                multiline
                rows={3}
                autoFocus
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {showOtherInput && (
          <Button
            onClick={onOtherSubmit}
            variant="contained"
            disabled={!otherText.trim()}
          >
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
